import {ReactNode} from "react";
import Section from "../Section";
import {Grid, Stack} from "@mui/material";
import {FormattedMessage, defineMessage, defineMessages} from "react-intl";
import {ThreeDots} from "@dvrt/graphql-widgets";
import {labelStyle, valueStyle} from "@/styles";
import BackendError from "../BackendError";
import {DomainSummaryQuery, useDomainSummaryQuery} from "./queries.generated";

type Props = {
    domainId: number;
};

type DomainSummary = DomainSummaryQuery["domainSummary"];
type DomainSummaryField = Exclude<keyof DomainSummary, "__typename">;

const DOMAIN_SUMMARY_FIELD_MESSAGES = defineMessages<DomainSummaryField>({
    name: {id: "name", defaultMessage: "Name"},
    description: {id: "description", defaultMessage: "Description"},
    shortNumbersLength: {id: "shortNumbersLength", defaultMessage: "Short number's length"},
    users: {id: "users", defaultMessage: "Users"},
    faxes: {id: "faxes", defaultMessage: "Faxes"},
    gateways: {id: "gateways", defaultMessage: "Gateways"},
    pickupGroups: {id: "pickupGroups", defaultMessage: "Pickup groups"},
    callGroups: {id: "callGroups", defaultMessage: "Call groups"},
    callQueues: {id: "callQueues", defaultMessage: "Call queues"},
    ivrs: {id: "ivrs", defaultMessage: "Auto attendants"},
});

export default ({domainId}: Props) => {
    const {loading, data, error} = useDomainSummaryQuery({
        variables: {domainId},
    });

    if (loading) return <ThreeDots height={5} />;

    if (error) return <BackendError error={error} />;

    function createItems(attributes: DomainSummaryField[]): ReactNode[] {
        const items = [];
        let i = 0;
        for (const attribute of attributes) {
            items.push(
                <Grid key={i} container>
                    <Grid style={labelStyle} size={2}>
                        <span>
                            <FormattedMessage {...DOMAIN_SUMMARY_FIELD_MESSAGES[attribute]} />:
                        </span>
                    </Grid>
                    <Grid style={{...valueStyle, textAlign: "right"}} size={2}>
                        <span>{data!.domainSummary[attribute]}</span>
                    </Grid>
                </Grid>,
            );
            i++;
        }
        return items;
    }

    return (
        <Stack spacing={3}>
            <Section label={defineMessage({id: "basic_info", defaultMessage: "Basic info"})}>
                {createItems(["name", "description", "shortNumbersLength"])}
            </Section>
            <Section label={defineMessage({id: "endpoints", defaultMessage: "Endpoints"})}>
                {createItems(["users", "faxes", "gateways", "pickupGroups", "callGroups", "callQueues", "ivrs"])}
            </Section>
        </Stack>
    );
};
